import { useEffect, useRef, useState } from "react";
import { MessageSquare, Mic, Sparkles, X } from "lucide-react";

/**
 * Floating Action Button with two-option speed-dial.
 * Click the main button → expands to show "Chat" + "Voice".
 * Click again (or outside) → collapses.
 */
export default function AIFab({ onChat, onVoice, primary = "#41B5E6", lang = "en" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isRTL = lang === "ar";
  const chatLabel = isRTL ? "محادثة نصية" : "Chat";
  const voiceLabel = isRTL ? "محادثة صوتية" : "Voice";
  const mainLabel = isRTL ? "اسأل AI" : "Ask AI";

  return (
    <div
      ref={rootRef}
      data-testid="ai-fab-root"
      className={`fixed bottom-6 ${isRTL ? "left-6" : "right-6"} flex flex-col ${isRTL ? "items-start" : "items-end"} gap-3`}
      style={{ zIndex: 10000 }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Sub-options */}
      <div
        className={`flex flex-col ${isRTL ? "items-start" : "items-end"} gap-3 transition-all duration-300 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <FabOption
          testid="fab-voice"
          label={voiceLabel}
          icon={<Mic className="w-5 h-5" />}
          color={primary}
          delay={open ? 60 : 0}
          isRTL={isRTL}
          onClick={() => { setOpen(false); onVoice?.(); }}
        />
        <FabOption
          testid="fab-chat"
          label={chatLabel}
          icon={<MessageSquare className="w-5 h-5" />}
          color={primary}
          delay={open ? 0 : 60}
          isRTL={isRTL}
          onClick={() => { setOpen(false); onChat?.(); }}
        />
      </div>

      {/* Main FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-fab-main"
        aria-label={mainLabel}
        aria-expanded={open}
        className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-white transition-transform duration-300 hover:scale-110 active:scale-95 ${
          open ? "rotate-90" : ""
        }`}
        style={{
          background: `linear-gradient(135deg, ${primary}, ${primary}dd)`,
          boxShadow: open
            ? `0 8px 32px ${primary}99, 0 0 0 6px ${primary}22`
            : `0 8px 28px ${primary}88, 0 0 18px ${primary}77`,
        }}
      >
        {/* Pulse ring */}
        <span
          aria-hidden
          className={`absolute inset-0 rounded-full ai-fab-pulse ${open ? "opacity-0" : "opacity-100"}`}
          style={{ background: `radial-gradient(circle, ${primary}55, transparent 70%)` }}
        />
        {open ? <X className="w-7 h-7 relative z-10" /> : <Sparkles className="w-7 h-7 relative z-10" />}
      </button>
    </div>
  );
}

function FabOption({ icon, label, color, onClick, delay = 0, testid, isRTL }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      style={{ transitionDelay: `${delay}ms` }}
      className={`group flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""} bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700/60 hover:border-white/40 rounded-full py-2 ${isRTL ? "pl-5 pr-2" : "pr-5 pl-2"} shadow-xl transition-all hover:scale-[1.04]`}
    >
      <span
        className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0"
        style={{
          background: `linear-gradient(135deg, ${color}, ${color}dd)`,
          boxShadow: `0 4px 14px ${color}88`,
        }}
      >
        {icon}
      </span>
      <span className="text-sm font-bold tracking-wide whitespace-nowrap">{label}</span>
    </button>
  );
}
